import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Amount,
  Badge,
  Box,
  Button,
  Heading,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  TabItem,
  TabList,
  TabPanel,
  Tabs,
  Text,
} from '@razorpay/blade/components';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { LoansPaymentDrawer } from './LoansPaymentDrawer';
import { DateFilter } from 'components/ApprovalWorkflows/MyRequests/components/DateFilter';
import {
  LOAN_STATUS,
  LOAN_STATUS_LABEL,
  LOAN_STATUS_COLOR,
} from 'components/LoansAndAdvances/constants';
import { useLoanData } from 'components/LoansAndAdvances/useLoanData';
import { LoanDetail } from './types';
import { TAB_STATUS_MAP, TAB_PENDING, TAB_COMPLETED } from './constants';
import {
  formatDate,
  formatMonthYearShort,
  getEmiEndMonth,
  getInitialTab,
  formatDateToYMD,
} from './utils';
import { useLoanDrawer } from 'components/LoansAndAdvances/useLoanDrawer';

export const LoanPaymentV2 = () => {
  const history = useHistory();
  const location = useLocation();
  const { loanId: selectedLoanId, openDrawer, closeDrawer } = useLoanDrawer();

  const [activeTab, setActiveTab] = useState<string>(() => getInitialTab(location.search));

  const urlParams = new URLSearchParams(location.search);
  const fromDate = urlParams.get('from_date') || undefined;
  const toDate = urlParams.get('to_date') || undefined;
  const isFiltered = !!(fromDate && toDate);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    const params = new URLSearchParams(location.search);
    params.delete('status');
    (TAB_STATUS_MAP[value] ?? []).forEach((s) => params.append('status', String(s)));
    history.replace({ search: params.toString() });
  };

  const formatShort = (d: Date) =>
    d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  const createdFrom = isFiltered ? formatDateToYMD(new Date(fromDate!)) : undefined;
  const createdTo = isFiltered ? formatDateToYMD(new Date(toDate!)) : undefined;
  const dateParams = isFiltered ? { created_from: createdFrom, created_to: createdTo } : {};

  const { data: pendingData, isFetching: isPendingFetching } = useLoanData(LOAN_STATUS.PENDING, {
    ...dateParams,
  });
  const { data: completedData, isFetching: isCompletedFetching } = useLoanData(
    [LOAN_STATUS.APPROVED, LOAN_STATUS.REJECTED],
    { ...dateParams },
  );

  const pendingLoans = (pendingData?.data ?? []) as LoanDetail[];
  const completedLoans = (completedData?.data ?? []) as LoanDetail[];

  const pendingCount = pendingData?.total ?? 0;
  const completedCount = completedData?.total ?? 0;

  return (
    <Box padding="spacing.8">
      <Heading size="large" marginBottom="spacing.7">
        Loans & Advances
      </Heading>

      <Box position="relative">
        <Box
          position="absolute"
          right="spacing.0"
          top="spacing.1"
          zIndex={1}
          display="flex"
          alignItems="center"
          gap="spacing.3">
          <DateFilter
            label={
              isFiltered
                ? `${formatShort(new Date(fromDate!))} – ${formatShort(new Date(toDate!))}`
                : 'All Time'
            }
          />
          {isFiltered && (
            <Button
              size="small"
              variant="secondary"
              color="negative"
              onClick={() => {
                const params = new URLSearchParams(location.search);
                params.delete('from_date');
                params.delete('to_date');
                history.replace({ search: params.toString() });
              }}>
              Clear
            </Button>
          )}
        </Box>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <TabList>
            <TabItem value={TAB_PENDING}>
              Pending Approvals{' '}
              {pendingCount > 0 && (
                <Badge size="small" color="neutral" marginLeft="spacing.2">
                  {String(pendingCount)}
                </Badge>
              )}
            </TabItem>
            <TabItem value={TAB_COMPLETED}>
              Completed{' '}
              {completedCount > 0 && (
                <Badge size="small" color="neutral" marginLeft="spacing.2">
                  {String(completedCount)}
                </Badge>
              )}
            </TabItem>
          </TabList>
          <TabPanel value={TAB_PENDING}>
            <Box paddingTop="spacing.5">
              <Table<LoanDetail>
                data={{ nodes: pendingLoans }}
                isLoading={isPendingFetching}
                gridTemplateColumns="1fr 1.4fr 0.8fr 0.8fr 1.5fr 1fr">
                {(tableData) => (
                  <>
                    <TableHeader>
                      <TableHeaderRow>
                        <TableHeaderCell>ID</TableHeaderCell>
                        <TableHeaderCell>Name</TableHeaderCell>
                        <TableHeaderCell>EMI Starts From</TableHeaderCell>
                        <TableHeaderCell>EMI End Month</TableHeaderCell>
                        <TableHeaderCell>Reason</TableHeaderCell>
                        <TableHeaderCell textAlign="right">Amount</TableHeaderCell>
                      </TableHeaderRow>
                    </TableHeader>
                    <TableBody>
                      {tableData.map((loan) => (
                        <TableRow key={loan.id} item={loan} onClick={() => openDrawer(loan.id)}>
                          <TableCell>
                            <Box display="flex" flexDirection="column">
                              <Text size="small" weight="semibold">{`#${loan.id}`}</Text>
                              <Text size="xsmall" color="surface.text.gray.muted">
                                {formatDate(loan.created_at)}
                              </Text>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" flexDirection="column">
                              <Text size="small">{loan.name ?? '—'}</Text>
                              {loan.employee_id && (
                                <Text size="xsmall" color="surface.text.gray.muted">
                                  {loan.employee_id}
                                </Text>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Text size="small">
                              {formatMonthYearShort(loan.recovery_start_from)}
                            </Text>
                          </TableCell>
                          <TableCell>
                            <Text size="small">
                              {getEmiEndMonth(loan.recovery_start_from, loan.tenure)}
                            </Text>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" flexDirection="column">
                              <Text size="small" truncateAfterLines={1}>
                                {loan.remarks || '—'}
                              </Text>
                              {loan.is_critical && (
                                <Text size="xsmall" color="surface.text.gray.muted">
                                  Critical
                                </Text>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell textAlign="right">
                            <Amount value={loan.principal} size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </>
                )}
              </Table>
              {!isPendingFetching && pendingLoans.length === 0 && (
                <EmptyListView
                  title="No pending approvals"
                  description={
                    <Text color="surface.text.gray.muted" textAlign="center">
                      No loan requests are awaiting your approval.
                    </Text>
                  }
                />
              )}
            </Box>
          </TabPanel>
          <TabPanel value={TAB_COMPLETED}>
            <Box paddingTop="spacing.5">
              <Table<LoanDetail>
                data={{ nodes: completedLoans }}
                isLoading={isCompletedFetching}
                gridTemplateColumns="1fr 1.4fr 1.5fr 0.8fr 1fr">
                {(tableData) => (
                  <>
                    <TableHeader>
                      <TableHeaderRow>
                        <TableHeaderCell>ID</TableHeaderCell>
                        <TableHeaderCell>Name</TableHeaderCell>
                        <TableHeaderCell>Reason</TableHeaderCell>
                        <TableHeaderCell>Status</TableHeaderCell>
                        <TableHeaderCell textAlign="right">Amount</TableHeaderCell>
                      </TableHeaderRow>
                    </TableHeader>
                    <TableBody>
                      {tableData.map((loan) => (
                        <TableRow
                          key={`${loan.id}-${loan.status}`}
                          item={loan}
                          onClick={() => openDrawer(loan.id)}>
                          <TableCell>
                            <Box display="flex" flexDirection="column">
                              <Text size="small" weight="semibold">{`#${loan.id}`}</Text>
                              <Text size="xsmall" color="surface.text.gray.muted">
                                {formatDate(loan.created_at)}
                              </Text>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" flexDirection="column">
                              <Text size="small">{loan.name ?? '—'}</Text>
                              {loan.employee_id && (
                                <Text size="xsmall" color="surface.text.gray.muted">
                                  {loan.employee_id}
                                </Text>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Text size="small" truncateAfterLines={1}>
                              {loan.remarks || '—'}
                            </Text>
                          </TableCell>
                          <TableCell>
                            <Badge color={LOAN_STATUS_COLOR[loan.status] ?? 'neutral'}>
                              {LOAN_STATUS_LABEL[loan.status] ?? 'Unknown'}
                            </Badge>
                          </TableCell>
                          <TableCell textAlign="right">
                            <Amount value={loan.principal} size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </>
                )}
              </Table>
              {!isCompletedFetching && completedLoans.length === 0 && (
                <EmptyListView
                  title="No completed requests"
                  description={
                    <Text color="surface.text.gray.muted" textAlign="center">
                      No approved or rejected loan requests yet.
                    </Text>
                  }
                />
              )}
            </Box>
          </TabPanel>
        </Tabs>
      </Box>

      <LoansPaymentDrawer
        isOpen={!!selectedLoanId}
        onDismiss={closeDrawer}
        loanId={selectedLoanId}
      />
    </Box>
  );
};
